import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Popup, { type PopupProps, type TriggerProps } from '@atlaskit/popup';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { LoadedRovoApp } from '../index';
import { useAppOAuth } from '../oauth/useAppOAuth';

import { messages } from './messages';

interface OAuthPopupWrapperProps {
	app: LoadedRovoApp;
	/** Where the Connect flow was started (e.g. filter dropdown vs Rovo settings side nav modal). */
	source?: string;
	/** Which product surface opened the flow (e.g. 'chat', 'search') for analytics attribution. */
	surface?: string;
	onAuthorizeAction?: () => void;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	trigger: (triggerProps: TriggerProps & { onClick?: () => void }) => JSX.Element;
	placement?: PopupProps['placement'];
}

const styles = cssMap({
	popupContent: {
		width: '20rem',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: token('radius.small'),
	},
	buttonContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		flexDirection: 'row',
	},
	button: {
		marginTop: token('space.100'),
		marginRight: token('space.100'),
		marginBottom: token('space.100'),
		marginLeft: token('space.100'),
	},
});

const OAuthPopupWrapperImpl = ({
	app,
	source = 'appFilterMenu',
	surface,
	onAuthorizeAction,
	onCompletedAction,
	onDismissAction,
	trigger,
	placement,
}: OAuthPopupWrapperProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();

	const { onAuthorize } = useAppOAuth({
		friendlyName: app.friendlyName,
		appSource: app.source,
		outboundAuthUrl: app.outboundAuthUrl || '',
		onCompletedAction: () => {
			setIsOpen(false);
			onCompletedAction?.();
		},
		onFailAction: () => {
			setIsOpen(false);
		},
		analyticsAttributes: {
			source,
			isNewlyEnabled: app.isNewlyEnabled,
			surface,
		},
	});

	const onDismiss = () => {
		setIsOpen(false);
		onDismissAction?.();
	};

	const handleTriggerClick = () => {
		if (!app.isUserOAuthed) {
			// When the one-click 3P connect experiment is enabled, skip the confirmation
			// popup and take the user straight to the OAuth auth flow.
			if (
				FeatureGates.getExperimentValue<boolean>(
					'rovo_chat_3p_connect_one_click',
					'isEnabled',
					false,
				) === true
			) {
				onAuthorizeAction?.();
				void onAuthorize();
				return;
			}
			setIsOpen(true);
		}
	};

	const handleAuthorizeClick = () => {
		onAuthorizeAction?.();
		void onAuthorize();
	};

	return (
		<Popup
			isOpen={isOpen}
			onClose={onDismiss}
			placement={placement}
			zIndex={600}
			trigger={(triggerProps) =>
				trigger({
					...triggerProps,
					onClick: handleTriggerClick,
				} as TriggerProps & { onClick?: () => void })
			}
			shouldFitContainer={false}
			content={() => (
				<Box xcss={styles.popupContent}>
					<Stack space="space.100">
						<Text>
							{formatMessage(messages.authorizeConnectApp, {
								appName: app.friendlyName,
								strong: (text: React.ReactNode) => <Text as="strong">{text}</Text>,
								br: <br />,
							})}
						</Text>
					</Stack>
					<Stack xcss={styles.buttonContainer}>
						<Stack xcss={styles.button}>
							<Button onClick={onDismiss}>{formatMessage(messages.notNow)}</Button>
						</Stack>
						<Stack xcss={styles.button}>
							<Button onClick={handleAuthorizeClick} appearance="primary">
								{formatMessage(messages.connect)}
							</Button>
						</Stack>
					</Stack>
				</Box>
			)}
		/>
	);
};

export const OAuthPopupWrapper = (props: OAuthPopupWrapperProps): React.JSX.Element => (
	<OAuthPopupWrapperImpl {...props} />
);
